import { computed, action } from "mobx";
import { ApiResult, FieldType, isEmptyOrWhitespace, ViewModelBase } from "@shoothill/core";
import moment from "moment";
import { HireTrackerItemModel, UpsertHireTrackerReportItemDTO } from "./HireTrackerItemModel";
import { AppUrls } from "AppUrls";
import type { ValidationResponse } from "@shoothill/core";

export class HireTrackerItemViewModel extends ViewModelBase<HireTrackerItemModel> {
    constructor(item: HireTrackerItemModel) {
        super(item);
    }

    // ----- Setters used by the view -----

    @action
    public setHireStatus = (id: string | null, displayName?: string | null) => {
        this.model.hireStatusId = id;
        // If a name is supplied, set it, otherwise leave as-is when id changes
        if (displayName !== undefined) {
            this.model.hireStatusName = displayName ?? "";
        }
    };

    @action
    public setoffHireDate = (date: Date | null) => {
        this.model.offHireDate = date;
    };

    @action
    public setHireNote = (note: string) => {
        this.model.hireNote = note;
    };

    // ----- Getters -----

    @computed
    public get id() {
        return this.model.id;
    }

    @computed
    public get itemId() {
        return this.model.itemId;
    }

    @computed
    public get quantity() {
        return this.model.quantity;
    }

    @computed
    public get unitPrice() {
        return this.model.unitPrice;
    }

    @computed
    public get description() {
        return this.model.description;
    }

    @computed
    public get supplierName() {
        return this.model.supplierName;
    }

    @computed
    public get formattedPONumber() {
        return this.model.formattedPONumber;
    }

    @computed
    public get hireStartDate() {
        return this.model.hireStartDate;
    }

    @computed
    public get hireEndDate() {
        return this.model.hireEndDate;
    }

    @computed
    public get total() {
        return this.model.total;
    }

    @computed
    public get duration() {
        return this.model.duration;
    }

    @computed
    public get hireStatusName() {
        return this.model.hireStatusName;
    }

    @computed
    public get hireStatusId() {
        return this.model.hireStatusId;
    }

    @computed
    public get offHireDate() {
        return this.model.offHireDate;
    }

    @computed
    public get hireNote() {
        return this.model.hireNote ?? "";
    }

    // ----- Formatted helpers -----

    @computed
    public get hireStartDateFormatted() {
        return this.model.hireStartDate ? moment(this.model.hireStartDate).format("DD/MM/YYYY") : "";
    }

    @computed
    public get hireEndDateFormatted() {
        return this.model.hireEndDate ? moment(this.model.hireEndDate).format("DD/MM/YYYY") : "";
    }

    @computed
    public get offHireDateFormatted() {
        return this.model.offHireDate ? moment(this.model.offHireDate).format("DD/MM/YYYY") : "";
    }

    @computed
    public get hirePeriodFormatted() {
        const start = this.hireStartDateFormatted;
        const end = this.hireEndDateFormatted;
        if (!start && !end) return "";
        if (start && !end) return `${start} -`;
        if (!start && end) return `- ${end}`;
        return `${start} - ${end}`;
    }

    @computed
    public get unitPriceFormatted() {
        return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(this.unitPrice ?? 0);
    }

    @computed
    public get totalFormatted() {
        return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(this.total ?? 0);
    }

    @computed
    public get durationFormatted() {
        const d = this.duration ?? 0;
        if (!d) return "";
        const plural = Math.abs(d) === 1 ? "week" : "weeks";
        return `${d} ${plural}`;
    }

    @action
    public upsertItemHireInformation = async (): Promise<ApiResult<any>> => {
        this.setIsLoading(true);

        const request: UpsertHireTrackerReportItemDTO = this.model.toUpsertDto();

        const apiResult = await this.Post<any>(AppUrls.Server.Report.IE.UpsertItemHireInformation, request);

        if (apiResult) {
            if (apiResult.wasSuccessful) {
                //this.history.push(AppUrls.Client.Approval.List);
            } else {
                console.log(apiResult.errors);
                // runInAction(() => {
                //     this.matchViewModel.setSnackMessage("Failed to process approval");
                //     this.matchViewModel.setSnackType(this.matchViewModel.SNACKERROR);
                //     this.matchViewModel.setSnackbarState(true);
                // });
            }
        }
        this.setIsLoading(false);
        return apiResult;
    };

    @computed
    private get validateHireStatusId(): ValidationResponse {
        const errorMessage = this.model.validateHireStatusId;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateOffHireDate(): ValidationResponse {
        const errorMessage = this.model.validateOffHireDate;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    public async isFieldValid(fieldName: keyof FieldType<HireTrackerItemModel>): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        switch (fieldName) {
            case "hireStatusId": {
                const result = this.validateHireStatusId;

                errorMessage = result.errorMessage;
                isValid = result.isValid;
                break;
            }
            // case "offHireDate": {
            //     const result = this.validateOffHireDate;

            //     errorMessage = result.errorMessage;
            //     isValid = result.isValid;
            //     break;
            // }
        }

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }
}
